<template>
  <!-- Tudo o que não é deste componente passa direto ao v-autocomplete: itens, rótulo,
       modelo, múltiplo. O @update:model-value daqui SOMA ao do chamador, não o troca —
       o Vue junta os dois ouvintes. -->
  <v-autocomplete v-bind="$attrs" v-model:search="search" @update:model-value="search = ''">
    <template v-for="name in forwardedSlots" :key="name" #[name]="scope">
      <slot :name="name" v-bind="scope || {}"/>
    </template>
    <!-- O "+" dentro do campo, junto da seta (decisão do usuário — por fora foi provado, e ficou pior):
         cria sem precisar digitar um nome que não existe. Leva o que foi digitado, se houver. O
         `mousedown.stop` é o que impede o campo de abrir o menu e focar a busca no mesmo clique. -->
    <template v-if="offersButton" #append-inner>
      <v-btn icon variant="text" size="small" density="comfortable" :title="$tt('new')"
             @mousedown.stop @click.stop="create">
        <v-icon>mdi-plus</v-icon>
      </v-btn>
    </template>
    <template #no-data>
      <v-list-item v-if="creatable && search && search.trim()" @click="create">
        <v-list-item-title>
          {{ $tt('action.create-instance') }} <strong>{{ search }}</strong>
        </v-list-item-title>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'

/**
 * O espaço do "+" no campo, em caracteres (`ch`): para quem dá largura ao campo pelo conteúdo — a do
 * relacionamento no formulário do item (`InstanceInput.sizeForProperty`) — não cortar o valor.
 */
export const CREATE_BUTTON_CHARS = 4

/** O atributo booleano ligado: `readonly` solto chega como `''`, e `:readonly="false"` como `false`. */
const isOn = (value: unknown) => value !== undefined && value !== null && value !== false

/**
 * Escolher entre o que existe, ou criar um item novo.
 *
 * Dois jeitos de criar, que emitem `create` com o texto digitado (aparado, e `''` sem texto): o "+"
 * dentro do campo, sempre à mão, e o "Criar: <texto>" da lista, quando a busca não acha nada. O que é
 * criado, e como, é problema de quem usa. É o comportamento do campo de relacionamento no formulário
 * de item, e também o do domínio no formulário de atributo.
 *
 * Existia em duas cópias, e uma guardava o texto digitado DENTRO do objeto da
 * propriedade (`property._currentText`) — estado de tela pendurado no modelo do design.
 * Aqui ele é estado do próprio componente.
 *
 * O texto é limpo a cada escolha: no múltiplo o Vuetify o deixaria no campo, e a
 * próxima busca começaria filtrada pelo nome do item que acabou de ser escolhido.
 */
@Component({name: 'ontos-creatable-autocomplete', emits: ['create'], inheritAttrs: false})
class CreatableAutocomplete extends Vue {
  /**
   * Se neste contexto se pode criar. Quem usa decide só isso; que o "Criar: <texto>" pede texto é
   * regra daqui. Era uma função do texto, e os três chamadores repetiam a mesma conferência do vazio.
   */
  @Prop({type: Boolean, default: true}) readonly creatable!: boolean

  search = ''

  /** Os slots do chamador, menos os deste componente. */
  get forwardedSlots(): string[] {
    return Object.keys(this.$slots).filter(name => name !== 'no-data' && name !== 'append-inner')
  }

  /** O "+" some no campo travado: somente leitura, ou desabilitado (a prévia em curso, por exemplo). */
  get offersButton(): boolean {
    return this.creatable && !isOn(this.$attrs.readonly) && !isOn(this.$attrs.disabled)
  }

  create() {
    this.$emit('create', (this.search || '').trim())
  }
}

export default toNative(CreatableAutocomplete)
</script>
